/**
 * Dataset utility functions for OpenSearch model mapping and configuration
 */

/**
 * Map dataset names to OpenSearch model types
 */
export function getModelTypeFromDataset(dataset: string): string {
  // SQL datasets (RFC 062) use their slug as the model type — the
  // sql-search endpoints are keyed by slug.
  if (isSqlDataset(dataset)) return dataset;

  switch (dataset) {
    case 'people':
      return 'Person';
    case 'companies':
      return 'Company';
    case 'tas-program-instances':
      return 'TasProgramInstance';
    case 'tas-program-templates':
      return 'TasProgramTemplate';
    case 'tas-event-transitions':
      return 'TasEventTransition';
    case 'pslf-forms':
      return 'PslfForm';
    case 'tas-audit-queues':
      return 'AuditQueue';
    case 'tas-audit-logs':
      return 'AuditLog';
    case 'syf-match-participants':
      return 'MatchParticipant';
    case 'syf-match-payments':
      return 'MatchPayment';
    case 'syf-match-participant-benefit-years':
      return 'MatchParticipantBenefitYear';
    case 'slr-contribution-plan-participants':
      return 'EmployeePlan';
    case 'loan-account-balance-snapshots':
      return 'ObservableEntitySnapshot';
    default:
      return 'Person';
  }
}

/**
 * Request model override for reporting search endpoints.
 * Persisted report config can keep the selected dataset model type while
 * outgoing /fields and /search payloads opt into application-level TAS data.
 */
export function getSearchRequestModelType(
  modelType: string,
  showIndividualApplicationsOnly = false
): string {
  if (modelType === 'TasProgramInstance' && showIndividualApplicationsOnly) {
    return 'TasApplication';
  }
  return modelType;
}

/**
 * Get the OpenSearch field name used for company filtering on a given dataset.
 * Returns undefined for datasets where company filtering is not applicable.
 */
export function getCompanyFilterField(dataset: string): string | undefined {
  // SQL datasets expose their tenant column as plain company_id in the
  // declared catalog; the backend additionally force-scopes by the
  // company_id request param regardless of client filters.
  if (isSqlDataset(dataset)) return 'company_id';

  switch (dataset) {
    case 'tas-program-instances':
      return 'company_id_keyword';
    case 'tas-program-templates':
      return 'tas_program_template_company_id_keyword';
    case 'tas-event-transitions':
      return 'company_id_keyword';
    case 'tas-audit-queues':
    case 'tas-audit-logs':
      return 'company_id_keyword';
    case 'pslf-forms':
      return 'pslf_form_company_id_keyword';
    case 'people':
      return 'company_ids';
    case 'companies':
      return 'company_id_keyword';
    case 'syf-match-participants':
      return 'syf_match_participant_company_id_keyword';
    case 'syf-match-payments':
      return 'syf_match_participant_company_id_keyword';
    case 'syf-match-participant-benefit-years':
      return 'syf_match_participant_benefit_year_company_id_keyword';
    case 'slr-contribution-plan-participants':
      return 'slr_contribution_plan_participant_company_id_keyword';
    // Person → company rollup; matches the backend company_filter_field and
    // the reserved ReportQueryBuilder::DATASET_COMPANY_FIELDS slug.
    case 'loan-account-balance-snapshots':
      return 'company_id_keyword';
    default:
      return undefined;
  }
}

/**
 * Reverse-map an OpenSearch model type back to a dataset key.
 * Returns undefined if no matching dataset is found.
 */
export function getDatasetFromModelType(modelType: string): string | undefined {
  if (isSqlDataset(modelType)) return modelType;

  const entry = AVAILABLE_DATASETS.find((d) => d.modelType === modelType);
  return entry?.key;
}

/**
 * Available datasets for reporting and charting
 */
export type ProductCode = 'TAS' | 'PSLF' | 'SYF' | 'SLR' | 'Core';

export const AVAILABLE_DATASETS = [
  { key: 'people', label: 'People Data', modelType: 'Person', product: 'Core' as ProductCode },
  {
    key: 'companies',
    label: 'Company Data',
    modelType: 'Company',
    product: 'Core' as ProductCode,
  },
  {
    key: 'tas-program-instances',
    label: 'Tuition Assistance Programs',
    modelType: 'TasProgramInstance',
    product: 'TAS' as ProductCode,
  },
  {
    key: 'tas-program-templates',
    label: 'Tuition Assistance Program Templates',
    modelType: 'TasProgramTemplate',
    product: 'TAS' as ProductCode,
  },
  {
    key: 'tas-event-transitions',
    label: 'TAS Event Funnel',
    modelType: 'TasEventTransition',
    product: 'TAS' as ProductCode,
  },
  {
    key: 'tas-audit-queues',
    label: 'TAS Audit Queue',
    modelType: 'AuditQueue',
    product: 'TAS' as ProductCode,
  },
  {
    key: 'tas-audit-logs',
    label: 'TAS Audit Measurements',
    modelType: 'AuditLog',
    product: 'TAS' as ProductCode,
  },
  { key: 'pslf-forms', label: 'PSLF Forms', modelType: 'PslfForm', product: 'PSLF' as ProductCode },
  {
    key: 'syf-match-participants',
    label: 'SYF Match Participants',
    modelType: 'MatchParticipant',
    product: 'SYF' as ProductCode,
  },
  {
    key: 'syf-match-payments',
    label: 'SYF Match Payments',
    modelType: 'MatchPayment',
    product: 'SYF' as ProductCode,
  },
  {
    key: 'syf-match-participant-benefit-years',
    label: 'SYF Benefit Year Participation',
    modelType: 'MatchParticipantBenefitYear',
    product: 'SYF' as ProductCode,
  },
  {
    key: 'slr-contribution-plan-participants',
    label: 'SLR Contribution Participants',
    modelType: 'EmployeePlan',
    product: 'SLR' as ProductCode,
  },
  {
    // Time-series Loan/Account balance snapshots (backend model
    // ObservableEntitySnapshot; tio-api#7904/#8116). Grouped under Core until
    // a dedicated product section is introduced. The mixed Loan/Account grain
    // is guarded server-side (SearchPolicy#validate_oes_aggregation_grain!):
    // any metric aggregation must be scoped to a single observable_entity_type
    // and is_latest_boolean=true, so a cross-grain double-count is rejected.
    key: 'loan-account-balance-snapshots',
    label: 'Loan & Account Balance Snapshots',
    modelType: 'ObservableEntitySnapshot',
    product: 'Core' as ProductCode,
  },
];

/**
 * SQL-backed datasets (RFC 062): published datasets fetched from
 * /api/v2/sql-datasets and registered by the app (tio-admin's report
 * routes) BEFORE the builder renders. Apps that never register (e.g.
 * tio-agent) see zero change — the registry stays empty and every lookup
 * falls through to the OpenSearch behavior. A module-level registry (not
 * a service) keeps the many synchronous AVAILABLE_DATASETS consumers
 * untouched.
 */
export type SqlDatasetEntry = {
  key: string;
  label: string;
  modelType: string;
  product: string;
  sourceKind: 'sql';
  crossCompanyCapable: boolean;
};

let sqlDatasetRegistry: SqlDatasetEntry[] = [];

export function registerSqlDatasets(
  datasets: Array<{
    slug: string;
    label: string;
    product?: string | null;
    crossCompanyCapable?: boolean;
  }>
) {
  // Slugs become dataset keys/model types — a collision with a classic
  // dataset key (e.g. 'people') would silently reroute an OpenSearch
  // dataset to sql-search and duplicate picker options. Reserved keys win.
  const reserved = new Set<string>(AVAILABLE_DATASETS.map((dataset) => dataset.key));
  sqlDatasetRegistry = datasets
    .filter((dataset) => {
      if (reserved.has(dataset.slug)) {
        console.error(
          `Ignoring SQL dataset with reserved slug '${dataset.slug}' — it collides with a classic dataset key`
        );
        return false;
      }
      return true;
    })
    .map((dataset) => ({
      key: dataset.slug,
      label: dataset.label,
      modelType: dataset.slug,
      product: dataset.product ?? 'Core',
      sourceKind: 'sql' as const,
      crossCompanyCapable: dataset.crossCompanyCapable ?? false,
    }));
}

export function sqlDatasets() {
  // Defensive copy: the registry must only change via registerSqlDatasets —
  // a consumer sort/push on the live array would corrupt routing session-wide.
  return [...sqlDatasetRegistry];
}

export function isSqlDataset(datasetOrModelType: string): boolean {
  return sqlDatasetRegistry.some((entry) => entry.key === datasetOrModelType);
}

export function isCrossCompanyCapable(datasetOrModelType: string): boolean {
  return sqlDatasetRegistry.some(
    (entry) => entry.key === datasetOrModelType && entry.crossCompanyCapable
  );
}

export function getProductForDataset(dataset: string): ProductCode {
  const entry = AVAILABLE_DATASETS.find((d) => d.key === dataset);
  if (entry) return entry.product;

  const sqlEntry = sqlDatasetRegistry.find((d) => d.key === dataset);
  if (sqlEntry && Object.hasOwn(PRODUCT_LABELS, sqlEntry.product)) {
    return sqlEntry.product as ProductCode;
  }

  return 'Core';
}

export function getReportTypeForDataset(dataset: string): string {
  return getProductForDataset(dataset).toLowerCase();
}

export const PRODUCT_LABELS: Record<ProductCode, string> = {
  TAS: 'Tuition Assistance',
  PSLF: 'PSLF',
  SYF: 'Secure',
  SLR: 'Loan Repayment',
  Core: 'Core',
};

export const PRODUCT_SECTION_ORDER: ProductCode[] = ['TAS', 'PSLF', 'SLR', 'SYF', 'Core'];

export const VALID_REPORT_TYPES = new Set(PRODUCT_SECTION_ORDER.map((code) => code.toLowerCase()));

/**
 * Comma-joined builder report types for the `filter[report_type]` query param
 * on the reports listing. jsonapi-resources turns this into a `WHERE IN`, so
 * the listing only fetches builder-supported products (not legacy
 * chart_builder/table_builder/aggregation_table reports). Derived from
 * VALID_REPORT_TYPES so the product set stays defined in one place.
 */
export const REPORT_TYPE_FILTER_VALUE = [...VALID_REPORT_TYPES].join(',');

/**
 * Time range options for filtering chart data
 */
export const TIME_RANGE_OPTIONS = [
  { key: 'all', label: 'All Data' },
  { key: '3m', label: 'Last 3 Months' },
  { key: '6m', label: 'Last 6 Months' },
  { key: '12m', label: 'Last 12 Months' },
  { key: '24m', label: 'Last 24 Months' },
  { key: '60m', label: 'Last 5 Years' },
];

/**
 * Get default sort fields for a dataset.
 * These are the OpenSearch field names (snake_case) used for ordering results.
 *
 * Note: Only use keyword-type fields for sorting. Text fields will cause
 * OpenSearch errors unless fielddata is enabled or a .keyword sub-field exists.
 * Use `_score:desc` when no reliable keyword sort field is available.
 */
export function getDefaultSortFields(dataset: string): string[] {
  switch (dataset) {
    case 'tas-program-instances':
      return ['tas_program_instance_created_at_date:desc', 'tas_application_created_at_date:desc'];
    case 'tas-event-transitions':
      return ['tas_event_transition_event_timestamp_date:desc'];
    case 'tas-audit-queues':
      return ['decided_at_date:desc'];
    case 'tas-audit-logs':
      return ['audited_at_date:desc'];
    case 'companies':
      return ['company_name_keyword:asc'];
    case 'loan-account-balance-snapshots':
      return ['snapshot_date_date:desc'];
    case 'pslf-forms':
    case 'tas-program-templates':
    case 'syf-match-participants':
    case 'slr-contribution-plan-participants':
    case 'syf-match-payments':
    case 'syf-match-participant-benefit-years':
    case 'people':
    default:
      // _score:desc is universally safe; _id tiebreaker is added by setSort()
      return ['_score:desc'];
  }
}

/**
 * Get essential fields that must always be included in query results for a dataset.
 * These are needed for routing, linking, and conditional logic in the table.
 */
export function getEssentialFields(dataset: string): string[] {
  switch (dataset) {
    case 'tas-program-instances':
      return [
        'tas_application_id_keyword',
        'tas_program_instance_id_keyword',
        'tas_program_template_id_keyword',
        'id',
        'tas_application_state_keyword',
        'tas_program_instance_state_keyword',
        'tas_application_created_at_date',
        'tas_program_instance_created_at_date',
        'company_id_keyword',
        'company_name_keyword',
      ];
    case 'tas-event-transitions':
      return [
        'id',
        'tas_event_transition_id_keyword',
        'tas_event_transition_application_id_keyword',
        'tas_event_transition_program_instance_id_keyword',
        'tas_event_transition_event_domain_keyword',
        'tas_event_transition_event_name_keyword',
        'tas_event_transition_event_timestamp_date',
        'company_id_keyword',
        'tas_event_transition_company_name_keyword',
      ];
    case 'pslf-forms':
      return [
        'id',
        'pslf_form_id_keyword',
        'pslf_form_status_keyword',
        'pslf_form_type_keyword',
        'pslf_form_company_id_keyword',
        'pslf_form_created_at_date',
        'pslf_form_person_id_keyword',
        'pslf_form_person_name_keyword_analyzable',
      ];
    case 'people':
      return ['id', 'person_id_keyword', 'person_first_name_text', 'person_last_name_text'];
    case 'tas-audit-queues':
      return [
        'id',
        'tas_application_id_keyword',
        'company_id_keyword',
        'audit_status_keyword',
        'decided_at_date',
      ];
    case 'tas-audit-logs':
      return [
        'id',
        'audit_queue_id_keyword',
        'tas_application_id_keyword',
        'company_id_keyword',
        'audited_at_date',
      ];
    case 'companies':
      return ['id', 'company_id_keyword', 'company_name_keyword', 'company_type_keyword'];
    case 'tas-program-templates':
      return [
        'id',
        'tas_program_template_company_id_keyword',
        'tas_program_template_company_name_keyword',
        'tas_program_template_created_at_date',
      ];
    case 'syf-match-participants':
      return [
        'id',
        'syf_match_participant_company_id_keyword',
        'syf_match_participant_enrollment_state_keyword',
        'syf_match_participant_created_at_date',
      ];
    case 'syf-match-payments':
      return [
        'id',
        'syf_match_payment_created_at_date',
        'syf_match_participant_company_id_keyword',
        'syf_match_participant_employee_id_keyword',
      ];
    case 'syf-match-participant-benefit-years':
      return [
        'id',
        'syf_match_participant_benefit_year_created_at_date',
        'syf_match_participant_benefit_year_company_id_keyword',
        'syf_match_participant_benefit_year_employee_id_keyword',
      ];
    case 'slr-contribution-plan-participants':
      return [
        'id',
        'slr_contribution_plan_participant_company_id_keyword',
        'slr_contribution_plan_participant_enrollment_status_keyword',
        'slr_contribution_plan_participant_created_at_date',
        'slr_contribution_plan_participant_updated_at_date',
      ];
    case 'loan-account-balance-snapshots':
      return [
        'id',
        'observable_entity_type_keyword',
        'observable_entity_id_keyword',
        'person_id_keyword',
        'company_id_keyword',
        'company_name_keyword',
        'snapshot_date_date',
        'is_latest_boolean',
      ];
    default:
      return ['id', 'created_at'];
  }
}

/**
 * Get default columns to display for each dataset when the user hasn't selected any.
 * Returns snake_case field names matching OpenSearch.  An empty array means
 * "show all columns" (fall through to the existing first-item-key logic).
 *
 * TAS default columns are defined separately in tas-column-config.ts
 * (DEFAULT_COLUMNS.individual / DEFAULT_COLUMNS.aggregate).
 * The entry here is only used for individual/document mode defaults.
 */
export function getDefaultColumns(dataset: string): string[] {
  switch (dataset) {
    case 'loan-account-balance-snapshots':
      // balance_amount_integer is cents (FE /100 via currency_cents?); guarded
      // against cross-grain sums server-side (see AVAILABLE_DATASETS note).
      return [
        'borrower_name_keyword',
        'company_name_keyword',
        'observable_entity_type_keyword',
        'balance_amount_integer',
        'forgiveness_program_keyword',
        'repayment_plan_keyword',
        'servicer_name_keyword',
        'snapshot_date_date',
      ];
    case 'tas-program-instances':
      // Individual mode defaults — matches DEFAULT_COLUMNS.individual in tas-column-config.ts.
      // Aggregate mode bypasses this entirely (see selectedColumnsArray in programs-page).
      return [
        'employee_name_keyword',
        'employee_payroll_id_keyword',
        'tas_program_template_field_program_name_keyword',
        'tas_program_instance_field_school_institution_name_keyword',
        'tas_application_id_keyword',
        'tas_program_instance_created_at_date',
      ];
    case 'companies':
      return [
        'company_name_keyword',
        'company_type_keyword',
        'company_is_active_boolean',
        'company_is_live_boolean',
        'company_products_enabled_keyword',
        'company_sub_domain_keyword',
        'company_launch_date_date',
        'company_partner_type_keyword',
        'company_created_at_date',
      ];
    case 'people':
      return [
        'person_first_name_text',
        'person_last_name_text',
        'all_employee_names_text',
        'all_emails',
        'all_company_names_keyword',
        'person_is_active_boolean',
        'person_is_enrolled_boolean',
        'all_employee_payroll_ids_text',
      ];
    case 'tas-event-transitions':
      return [
        'tas_event_transition_event_timestamp_pt_date',
        'tas_event_transition_actor_name_keyword',
        'tas_event_transition_actor_category_keyword',
        'tas_event_transition_decision_keyword',
        'tas_event_transition_event_name_keyword',
        'tas_event_transition_application_id_keyword',
        'tas_event_transition_employee_name_keyword',
        'tas_event_transition_company_name_keyword',
        'tas_event_transition_reason_amount_integer',
        'tas_event_transition_reason_note_keyword',
      ];
    case 'pslf-forms':
      return [
        'pslf_form_borrower_name_keyword_analyzable',
        'pslf_form_employer_name_keyword_analyzable',
        'pslf_form_type_keyword',
        'pslf_form_status_keyword',
        'pslf_form_created_at_date',
        'pslf_form_updated_at_date',
      ];
    case 'tas-audit-queues':
      return [
        'applicant_name_keyword',
        'company_name_display_keyword',
        'program_name_display_keyword',
        'action_taken_keyword',
        'decided_by_keyword',
        'audit_reason_keyword',
        'audit_status_keyword',
        'audit_outcome_keyword',
        'requested_amount_cents_integer',
        'decided_at_date',
      ];
    case 'tas-audit-logs':
      return [
        'company_name_display_keyword',
        'program_name_display_keyword',
        'audited_group_keyword',
        'audit_reason_keyword',
        'decision_correct_boolean',
        'correction_applied_boolean',
        'approved_by_group_cents_integer',
        'wrongly_approved_cents_integer',
        'wrongly_denied_cents_integer',
        'audited_at_date',
      ];
    case 'tas-program-templates':
      return [
        'tas_program_template_name_text',
        'tas_program_template_type_keyword_analyzable',
        'tas_program_template_company_name_keyword',
        'tas_program_template_state_keyword_analyzable',
        'tas_program_template_created_at_date',
      ];
    case 'syf-match-participants':
      return [
        'syf_match_participant_person_name_text',
        'syf_match_participant_company_name_keyword',
        'syf_match_participant_enrollment_state_keyword',
        'syf_match_participant_plan_name_text',
        'syf_match_participant_created_at_date',
      ];
    case 'syf-match-payments':
      return [
        'syf_match_payment_transaction_date_date',
        'syf_match_participant_person_name_text',
        'syf_match_participant_company_name_keyword',
        'syf_match_participant_plan_code_keyword',
        'syf_match_participant_benefit_year_integer',
        'syf_match_payment_transaction_amount_integer',
        'syf_match_payment_payment_qualified_status_keyword',
        'syf_match_payment_payment_certification_status_keyword',
      ];
    case 'syf-match-participant-benefit-years':
      return [
        'syf_match_participant_benefit_year_person_name_text',
        'syf_match_participant_benefit_year_company_name_keyword',
        'syf_match_participant_benefit_year_plan_code_keyword',
        'syf_match_participant_benefit_year_integer',
        'syf_match_participant_benefit_year_total_payment_count_integer',
        'syf_match_participant_benefit_year_total_paid_amount_integer',
        'syf_match_participant_benefit_year_completion_bucket_keyword',
      ];
    case 'slr-contribution-plan-participants':
      return [
        'slr_contribution_plan_participant_first_name_text',
        'slr_contribution_plan_participant_last_name_text',
        'slr_contribution_plan_participant_company_name_keyword',
        'slr_contribution_plan_participant_enrollment_status_keyword',
        'slr_contribution_plan_participant_plan_name_text',
        'slr_contribution_plan_participant_created_at_date',
      ];
    default:
      // Empty = show all columns (existing behavior)
      return [];
  }
}
